/**
 * Phase 45 Plan 45-08 — task activity timeline API client.
 *
 * Backend contract (45-08 — new in this plan):
 *   GET /api/tasks/:taskId/activity → TaskActivityEvent[]
 *
 * Backend aggregates StateChange rows across the five task-scoped namespaces
 * (task, task_assignee, task_acceptance, task_priority, task_lifecycle) and
 * returns the latest 50 ordered DESC by createdAt. UI renders human-readable
 * labels via the (subjectType, state) discriminator in TaskActivityTimeline.
 */
import { apiGet } from './client';

export interface TaskActivityEvent {
  id: number;
  subjectType: string;
  subjectId: number;
  state: string;
  reason: string | null;
  // JSONB payload — shape varies per (subjectType, state). See
  // backend/src/services/stateChangeService.ts SubjectType union docstring.
  changes: Record<string, unknown> | unknown[] | null;
  createdAt: string | null;
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  // Quick task 260809-qcy — resolved from `changes.actor` for rows written
  // by an external client (attachment uploads, client-submitted intake).
  // Optional so existing test fixtures and consumers compile unchanged.
  externalActor?: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  // The Factory™ builder agent that ran the task (e.g. "Jan Kubica (bot)") on
  // rows the Factory™ system actor wrote after a dispatch to that agent.
  // Optional so existing test fixtures and consumers compile unchanged.
  agentName?: string | null;
}

export async function fetchTaskActivity(
  taskId: number,
): Promise<TaskActivityEvent[]> {
  const response = await apiGet<{ data: TaskActivityEvent[] }>(
    `/tasks/${taskId}/activity`,
  );
  return response.data;
}
